// Welt-Uhr: Umrechnung der Serverzeit (Unix-Millisekunden) in Tageszeit und Helligkeit.
// Reine Funktionen ohne Zustand, damit Client und Server dasselbe Ergebnis liefern.
import { clamp, lerp } from '../util/math.js';

/** Länge eines Spieltags in Millisekunden: zwanzig Minuten von Mitternacht zu Mitternacht. */
export const DAY_LENGTH_MS = 20 * 60 * 1000;

/**
 * Länge eines Hoftags in Hof-Sekunden: zwanzig Minuten, so lang wie ein Tag der Welt. Anders als
 * die Tageszeit zählt die Hof-Uhr nur, solange ihr Besitzer da ist (task-015, ADR 0029). Wartezeiten
 * auf dem Hof – die Kuh eine Minute, der Apfelbaum zwei Hoftage – sind Zielwerte auf dieser Uhr.
 */
export const FARM_DAY_SECONDS = DAY_LENGTH_MS / 1000;

/**
 * Der Hoftag zum Stand der Hof-Uhr, ab 1 gezählt: `floor(farmSeconds / FARM_DAY_SECONDS) + 1`.
 * Unbrauchbare Werte (negativ, NaN, unendlich) ergeben Hoftag 1.
 */
export function farmDay(farmSeconds: number): number {
  if (!Number.isFinite(farmSeconds) || farmSeconds < 0) return 1;
  return Math.floor(farmSeconds / FARM_DAY_SECONDS) + 1;
}

/** Beginn und Ende der Morgendämmerung als Tagesanteil; der Abend liegt spiegelbildlich dazu. */
const DAWN_START = 0.2;
const DAWN_END = 0.3;

/**
 * Tageszeit als Anteil in [0, 1): 0 = Mitternacht, 0.5 = Mittag. Hängt nur von der echten Uhrzeit
 * ab (Serverzeit modulo Taglänge), ein Neustart des Servers verschiebt sie also nicht.
 * Negative Zeiten liegen vor der Epoche und laufen sauber rückwärts; unbrauchbare Werte
 * (NaN, unendlich) ergeben Mitternacht statt NaN.
 */
export function dayFraction(serverTimeMs: number): number {
  if (!Number.isFinite(serverTimeMs)) return 0;
  const rest = ((serverTimeMs % DAY_LENGTH_MS) + DAY_LENGTH_MS) % DAY_LENGTH_MS;
  const fraction = rest / DAY_LENGTH_MS;
  return fraction < 1 ? fraction : 0;
}

/** Hermite-Glättung: 0 bei `edge0`, 1 bei `edge1`, dazwischen weich mit waagerechten Enden. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

/**
 * Helligkeit in [0, 1] zur Tageszeit `fraction`: Nacht (0) zwischen 0.0 und 0.2 sowie 0.8 und 1.0,
 * Tag (1) zwischen 0.3 und 0.7, dazwischen Dämmerung per smoothstep. Symmetrisch um den Mittag,
 * weil nur der Abstand zu 0.5 eingeht: daylight(f) == daylight(1 - f).
 */
export function daylight(fraction: number): number {
  const abstandVomMittag = Math.abs(fraction - 0.5);
  return 1 - smoothstep(0.5 - DAWN_END, 0.5 - DAWN_START, abstandVomMittag);
}

/**
 * Glättung der Offset-Schätzung im Client (Serverzeit = Date.now() + offset). Jede Uhr-Nachricht
 * liefert eine Messung `sampleOffset`; die Schätzung nähert sich ihr um den Anteil `alpha` in [0, 1].
 * Ohne bisherige Schätzung (`null`) wird die erste Messung vollständig übernommen.
 */
export function clockOffsetUpdate(
  currentOffset: number | null,
  sampleOffset: number,
  alpha: number,
): number {
  if (currentOffset === null) return sampleOffset;
  return lerp(currentOffset, sampleOffset, clamp(alpha, 0, 1));
}
